// Split Transcribe items into speaker-pure pieces (TT-1102).
//
// The keeper does this in speakerTimeline.js with a wall-clock timeline of
// Zoom activeSpeaker events supplying names. The host lane has no such
// source — the labels are all it has — so a label becomes "Speaker N".
// Runs and reassembly are the keeper's, verbatim in behaviour, so the drips
// this lane emits are shaped like everyone else's. The unit is a slice of
// items, not a result: since TT-1103 a result is dripped in stable-sentence
// slices as it forms (stableDrips.js), the FINAL being only the last one.

export const UNLABELED_SPEAKER = "Speaker";

export function speakerName(label) {
  if (label == null || label === "") return UNLABELED_SPEAKER;
  const n = Number(label);
  return Number.isFinite(n) ? `Speaker ${n + 1}` : `Speaker ${label}`;
}

// Runs of one speaker label. Punctuation carries no label/timestamps and
// attaches to the current run. [] when the items are unlabeled.
export function itemRuns(items) {
  const runs = [];
  let cur = null;
  for (const it of items || []) {
    const isWord = it.Type !== "punctuation";
    if (isWord && it.Speaker == null) return [];
    if (isWord && (!cur || cur.label !== it.Speaker)) {
      cur = { label: it.Speaker, start: it.StartTime, end: it.EndTime, items: [] };
      runs.push(cur);
    }
    if (!cur) continue;
    cur.items.push(it);
    if (it.EndTime != null) cur.end = it.EndTime;
  }
  return runs;
}

// Words joined by spaces, punctuation attached — Transcribe's own
// `Transcript` rendering of the same items.
export function runText(items) {
  let out = "";
  for (const it of items) {
    if (it.Type === "punctuation") out += it.Content;
    else out += (out ? " " : "") + it.Content;
  }
  return out;
}

// A slice of one result's items → [{ speaker, text, start, end }],
// media-timeline seconds.
export function attributeItems(items) {
  const list = items || [];
  const runs = itemRuns(list);
  if (!runs.length) {
    // unlabeled: one piece spanning the words
    const text = runText(list);
    if (!text) return [];
    const words = list.filter((it) => it.Type !== "punctuation");
    const start = words.length ? words[0].StartTime ?? null : null;
    const end = words.length ? words[words.length - 1].EndTime ?? start : null;
    return [{ speaker: UNLABELED_SPEAKER, text, start, end }];
  }
  return runs
    .map((run) => ({
      speaker: speakerName(run.label),
      text: runText(run.items),
      start: run.start ?? null,
      end: run.end ?? run.start ?? null,
    }))
    .filter((piece) => piece.text);
}

// A whole result → pieces (Transcribe always ships the items).
export function attributeFinal(result) {
  return attributeItems(result?.Alternatives?.[0]?.Items);
}
